import { Component } from "react";
import { resolveBlankLogoAsset } from "./blankLogoAssets";
// Deliberately from scoreboardDefaults.js, not ScoreboardFrame.jsx: as of
// #1798, ScoreboardFrame itself renders a nested ScoreboardErrorBoundary
// around its content slot, so importing the constant back from
// ScoreboardFrame.jsx here would be a two-file circular import. See
// scoreboardDefaults.js's header.
import { DEFAULT_BACKGROUND_COLOR } from "./scoreboardDefaults";

// Card #1798 AC2 — a React error boundary whose fallback is Blank, never a
// stack trace or a blank white page. Deliberately self-contained rather than
// reusing BlankLayout/BlankLayout.css: BlankLayout's `.scoreboard-blank` is
// `width/height: 100%` and relies on an ancestor (ScoreboardFrame's
// `.scoreboard-frame__content`, itself inside the viewport-fixed
// `.scoreboard-frame`) to actually size and darken it. A render exception
// can be thrown by ANYTHING in the tree this boundary wraps, including code
// that never reaches that ancestor's DOM at all — so this fallback carries
// its own fixed/full-viewport/dark-background styling inline, and resolves
// the logo through the exact same allow-list BlankLayout does, so it is
// correct standing completely alone. Same data-testids as BlankLayout
// (`scoreboard-blank`/`scoreboard-blank-logo`) on purpose: a Cypress
// assertion that "the board shows Blank" doesn't need to know or care
// whether Blank came from the normal dispatch or from a caught exception.
//
// Card #2498, Opus judge finding 1 (2026-09-16): this component has FOUR
// call sites and only three of them are actually "standing completely
// alone" the way the paragraph above describes:
//   - App.jsx's two ROUTE-level boundaries (lines 170, 178) wrap the whole
//     page, outside ScoreboardFrame/the design canvas entirely,
//     real-viewport vw/vh IS correct there, unchanged.
//   - ScoreboardPage.jsx's boundary also wraps ScoreboardFrame from
//     outside; real-viewport vw/vh is correct there too, unchanged. It is
//     behaviourally right on the "viewport" default for a different reason
//     than the App.jsx pair: when IT catches, ScoreboardFrame hasn't
//     rendered (or has already unmounted) its own canvas element, so there
//     is no canvas to size against and real-viewport sizing is exactly
//     right.
//   - ScoreboardFrame.jsx nests a FOURTH instance around `children` INSIDE
//     `.scoreboard-frame__canvas` (the fixed 1920x1080 design canvas, itself
//     scaled by the frame's own transform). `.scoreboard-frame__canvas` has
//     `container-type: size`, which implies `contain: layout`, which makes
//     the canvas the containing block for this fallback's `position: fixed`
//     box — so `100vw`/`100vh`/`60vh`/`80vw` here would size against the
//     REAL viewport (vw/vh always resolve against the viewport, never an
//     ancestor), then get clipped by the canvas's `overflow: hidden`, then
//     the whole already-wrong box gets scaled AGAIN by the canvas transform.
//     At 2:1 (1500x750, scale 0.694) that lands the fallback in the
//     upper-left of the board at ~69% size instead of centred and full —
//     no error text (the never-show-an-error doctrine still holds), but not
//     the full, centred, readable board AC8 asks for either.
// `variant` picks between the two shapes: "viewport" (default, the three
// outside-the-canvas call sites above) keeps position:fixed + vw/vh sized
// against the real screen. "canvas" (ScoreboardFrame.jsx's nested usage)
// sizes against the design canvas's content slot instead, position:absolute
// (not fixed: this fallback's nearest positioned ancestor is
// `.scoreboard-frame__content`, which is `position: relative`
// (ScoreboardFrame.css), so absolute is what actually resolves against it,
// not a "either position value" equivalence) with inset:0 filling that
// content slot (1920 x 939.6 at scale 1, i.e. the canvas minus the 140.4px
// footer banner, not the full 1920x1080 canvas box) with no vw/vh at all,
// and the logo at the canvas-px equivalents of 60vh/80vw (648px/1536px at
// 1920x1080) — mirrors ScoreboardFrame's own existing `variant="embedded"`
// convention (ScoreboardFrame.jsx) rather than inventing a new shape.
// Rejected: a blind unit swap (px constants used unconditionally, dropping
// the App.jsx boundaries' correctness) — those two DO still need real-
// viewport sizing, so a single hardcoded style would just move the bug
// rather than fix it, and the canvas-relative fallback would also be wrong
// pre-canvas-mount (App.jsx's boundaries can catch an error before
// ScoreboardFrame ever renders its own canvas element at all).
const FALLBACK_STYLE_BY_VARIANT = {
  viewport: {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: DEFAULT_BACKGROUND_COLOR,
  },
  canvas: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: DEFAULT_BACKGROUND_COLOR,
  },
};

const FALLBACK_LOGO_STYLE_BY_VARIANT = {
  viewport: {
    height: "60vh",
    maxWidth: "80vw",
    objectFit: "contain",
  },
  canvas: {
    // 60vh/80vw's design-canvas px equivalent at 1920x1080 (1vh = 10.8px,
    // 1vw = 19.2px), same mechanical conversion AC2 applied throughout the
    // rest of the scoreboard tree.
    //
    // Card #2502: cq units, not px, so this fallback stays the same size as
    // BlankLayout at every canvas shape (BlankLayout.css's two @container
    // blocks), since a filled 4:3 board puts it in a 1440x1080 canvas and a 2:1
    // board in 1920x960. Inline styles cannot carry an @container query, but
    // the nearest size container of this variant is always the canvas (or the
    // letterbox box, 1920x1080), so 60cqh / 90cqw resolve against it. At the
    // 16:9 canvas they are exactly the old values (60cqh = 648px, and
    // min(1536px, 90cqw = 1728px) = 1536px), so 16:9 is unchanged.
    height: "min(648px, 60cqh)",
    maxWidth: "min(1536px, 90cqw)",
    objectFit: "contain",
  },
};

export default class ScoreboardErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // #1798 AC8 — "falling back to Blank still surfaces the fault to the
  // remote control." A render exception is the one failure mode this card's
  // remote-side classifyBoardRenderState (boardRenderState.js) CANNOT detect
  // from the composed payload's shape alone — the mode/fields can look
  // perfectly valid and still blow up a layout component on a bad nested
  // value. ScoreboardPage.jsx closes that gap by wrapping its own real,
  // visible board in two nested boundaries and reading the result back through
  // `onRenderStatus` + `postScoreboardStatus` — this callback is that
  // read-back point, not a second, independent diagnostic channel.
  // console.error also runs unconditionally so anything watching this
  // kiosk's own devtools/log capture still sees it, but that is
  // belt-and-braces, not the operator-facing half of AC8.
  componentDidCatch(error, info) {
    console.error("[scoreboard] render error, falling back to Blank", error, info?.componentStack);
    this.props.onRenderStatus?.(true);
  }

  componentDidMount() {
    // NOTE: componentDidMount DOES still run even on an instance whose very
    // first render threw — React commits the boundary fiber with the
    // fallback, runs this lifecycle, and only THEN processes the error
    // update queue and calls componentDidCatch above. This method is
    // correct only because of the `!this.state.hasError` guard below, not
    // because mount is somehow skipped; don't remove the guard on the
    // strength of "mount never fires on a caught first-render error", that
    // premise is false. With the guard, this is the "render succeeded" half
    // of the onRenderStatus contract, and componentDidCatch is the "render
    // threw" half.
    if (!this.state.hasError) this.props.onRenderStatus?.(false);
  }

  componentDidUpdate(prevProps) {
    // The public board (ScoreboardPage.jsx) never remounts this boundary —
    // it persists across polls and passes a `resetKey` (the latest
    // `serverTime`) so a caught error clears the moment fresh data arrives,
    // giving the board a chance to recover instead of staying on Blank
    // forever once one bad payload has been through it. This recovery
    // mechanism itself is unchanged by #1798's postMessage work below — it
    // still just clears `hasError` off a resetKey change, no remount, no
    // loop.
    //
    // #1798 AC8 — the `onRenderStatus(false)` call is new: componentDidMount
    // only ever fires once per instance, so without this, a caller tracking
    // "is the board currently render-erroring" (ScoreboardPage's own
    // postMessage status) would see `true` forever after the FIRST caught
    // error, even once this exact recovery has silently fixed it on a later
    // poll. If the next render throws again, componentDidCatch above fires
    // again right after and reasserts `true` — this optimistic `false` is
    // never the last word when the fault persists.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
      this.props.onRenderStatus?.(false);
    }
  }

  render() {
    if (this.state.hasError) {
      // Card #2311 (PROD HOTFIX) — resolveBlankLogoAsset's chain is now
      // stored pick -> theme logo -> null (no Fox Village mark left), so a
      // render error on a themed board with no other logo configured must
      // render NO <img> at all here too, same as BlankLayout's own fix.
      const resolvedLogoUrl = resolveBlankLogoAsset(this.props.logoAssetUrl, this.props.theme);
      // #2498 — "viewport" (App.jsx's route-level usage) or "canvas"
      // (ScoreboardFrame.jsx's nested usage, inside the design canvas). See
      // this file's header comment for why the two need different styling.
      const variant = this.props.variant === "canvas" ? "canvas" : "viewport";
      return (
        <div style={FALLBACK_STYLE_BY_VARIANT[variant]} data-testid="scoreboard-blank" data-blank-source="render-error">
          {resolvedLogoUrl ? (
            <img
              style={FALLBACK_LOGO_STYLE_BY_VARIANT[variant]}
              src={resolvedLogoUrl}
              alt=""
              data-testid="scoreboard-blank-logo"
            />
          ) : null}
        </div>
      );
    }
    return this.props.children;
  }
}
